import { immer } from 'zustand/middleware/immer'

import { getOnlyDate } from '~/astra'

import { Slice, StoreState } from '../store'
import { DrawerActions, DrawerState } from './types'

export const tabs = [
    { label: 'Status', value: 'overview' },
    { label: 'Venteliste', value: 'waitinglist' },
    { label: 'Ønsker', value: 'wishes' },
    { label: 'Om Hero', value: 'about' },
] as const
export type TabValue = (typeof tabs)[number]['value']

// Making readonly to allow options to shape the type signature
export const dateRangeOptions = [
    { label: '1 uke', value: '1' },
    { label: '2 uker', value: '2' },
    { label: '3 uker', value: '3' },
    { label: '4 uker', value: '4' },
    { label: 'Måned', value: 'm' },
] as const
export type DateRange = (typeof dateRangeOptions)[number]['value']

type MinimalSchedule = {
    date: string
    practitionerId: number
}

export type MinimalStatus = MinimalSchedule & {
    color?: string
    status_code: string
    label?: string
}

export type MinimalLocationSchedule = MinimalSchedule & {
    room_code: string
    location_id: number
}

export const showLocationsModes = ['combine', 'separate'] as const
export type ShowLocationsDisplayMode = (typeof showLocationsModes)[number]
export const createMinimalStatusId = (status: MinimalStatus) => `${status.date}-${status.practitionerId}-${status.status_code}`
export const createMinimalLocationScheduleId = (status: Pick<MinimalLocationSchedule, 'date' | 'practitionerId' | 'room_code'>) =>
    `${status.date}-${status.practitionerId}-${status.room_code}`

type State = {
    pendingStatuses: Record<string, MinimalStatus>
    pendingLocationSchedules: Record<string, MinimalLocationSchedule>

    dateRange: DateRange
    fullWidthCells: boolean
    isPopoverOpen: boolean
    showLocations: boolean
    showLocationsMode: ShowLocationsDisplayMode
    showSurgeonCounts: boolean
    showTableFooters: boolean
    showOnlyAvailableWaitingListPatients: boolean

    deselectedRecurringAssignmentUuids: string[]
} & DrawerState<TabValue>

const initialState: State = {
    pendingStatuses: {},
    pendingLocationSchedules: {},
    isPopoverOpen: false, // Popover is the component for the running the model
    fullWidthCells: false, // Whether the cells in the scheduler should be full width
    dateRange: 'm', // Default view for dates in the scheduler. m stands for month while numbers represent amount of weeks
    isDrawerOpen: false, // Drawer is the sidepanel with information about hero
    activeDrawerTab: 'overview',

    // Settings of the practitioner table (whether counts/locations/footers are shown)
    showLocations: true,
    showLocationsMode: 'separate',
    showSurgeonCounts: true,
    showTableFooters: true,

    // Additional settings of waiting lists per
    showOnlyAvailableWaitingListPatients: true,

    deselectedRecurringAssignmentUuids: [],
}

type Actions = {
    actions: {
        addBatchPendingStatus: (statuses: MinimalStatus[]) => void
        deleteBatchPendingStatus: (ids: string[]) => void

        addBatchPendingLocationSchedule: (statuses: MinimalLocationSchedule[]) => void
        deleteBatchPendingLocationSchedule: (ids: string[]) => void

        toggleLocations: () => void
        setShowLocationsMode: (mode: ShowLocationsDisplayMode) => void
        toggleSurgeonCounts: () => void
        toggleTableFooters: () => void

        setDateRange: (dateRange: DateRange) => void

        openPopover: () => void
        closePopover: () => void
        setPopover: (isOpen: boolean) => void

        toggleOnlyAvailableWaitingListPatients: () => void
        toggleFullWidthCells: () => void

        setDeselectedRecurringAssignmentUuids: (uuids: string[]) => void
    } & DrawerActions<TabValue, undefined>
}

export type SurgeonSchedulerSlice = {
    surgeonScheduler: State & Actions
}

export const createSurgeonSchedulerSlice: Slice<SurgeonSchedulerSlice> = immer(set => ({
    surgeonScheduler: {
        ...initialState,
        actions: {
            addBatchPendingStatus: statuses => {
                set(state => {
                    statuses.forEach(status => {
                        const id = createMinimalStatusId(status)
                        state.surgeonScheduler.pendingStatuses[id] = status
                    })
                })
            },
            deleteBatchPendingStatus: ids => {
                set(state => {
                    ids.forEach(id => {
                        delete state.surgeonScheduler.pendingStatuses[id]
                    })
                })
            },
            addBatchPendingLocationSchedule: statuses => {
                set(state => {
                    statuses.forEach(status => {
                        const id = createMinimalLocationScheduleId(status)
                        state.surgeonScheduler.pendingLocationSchedules[id] = status
                    })
                })
            },
            deleteBatchPendingLocationSchedule: ids => {
                set(state => {
                    ids.forEach(id => {
                        delete state.surgeonScheduler.pendingLocationSchedules[id]
                    })
                })
            },
            openDrawer: () => {
                set(state => {
                    state.surgeonScheduler.isDrawerOpen = true
                })
            },
            closeDrawer: () => {
                set(state => {
                    state.surgeonScheduler.isDrawerOpen = false
                })
            },
            toggleDrawer: () => {
                set(state => {
                    state.surgeonScheduler.isDrawerOpen = !state.surgeonScheduler.isDrawerOpen
                })
            },
            setActiveDrawerTab: (tabValue: TabValue) => {
                set(state => {
                    state.surgeonScheduler.activeDrawerTab = tabValue
                })
            },
            toggleLocations: () => {
                set(state => {
                    state.surgeonScheduler.showLocations = !state.surgeonScheduler.showLocations
                })
            },
            setShowLocationsMode: (mode: ShowLocationsDisplayMode) => {
                set(state => {
                    state.surgeonScheduler.showLocationsMode = mode
                })
            },
            toggleSurgeonCounts: () => {
                set(state => {
                    state.surgeonScheduler.showSurgeonCounts = !state.surgeonScheduler.showSurgeonCounts
                })
            },
            toggleTableFooters: () => {
                set(state => {
                    state.surgeonScheduler.showTableFooters = !state.surgeonScheduler.showTableFooters
                })
            },
            setDateRange: (dateRange: DateRange) => {
                set(state => {
                    state.surgeonScheduler.dateRange = dateRange
                })
            },
            openPopover: () => {
                set(state => {
                    state.surgeonScheduler.isPopoverOpen = true
                })
            },
            closePopover: () => {
                set(state => {
                    state.surgeonScheduler.isPopoverOpen = false
                })
            },
            setPopover: isOpen => {
                set(state => {
                    state.surgeonScheduler.isPopoverOpen = isOpen
                })
            },
            toggleOnlyAvailableWaitingListPatients: () => {
                set(state => {
                    state.surgeonScheduler.showOnlyAvailableWaitingListPatients = !state.surgeonScheduler.showOnlyAvailableWaitingListPatients
                })
            },
            toggleFullWidthCells: () => {
                set(state => {
                    state.surgeonScheduler.fullWidthCells = !state.surgeonScheduler.fullWidthCells
                })
            },
            setDeselectedRecurringAssignmentUuids: (uuids: string[]) => {
                set(state => {
                    state.surgeonScheduler.deselectedRecurringAssignmentUuids = uuids
                })
            },
        },
    },
}))

export const selectPendingStatusesForCell =
    (practitionerId: number, date: string) =>
    (state: StoreState): MinimalStatus[] =>
        Object.values(state.surgeonScheduler.pendingStatuses).filter(status => status.date === date && status.practitionerId === practitionerId)

export const selectPendingLocationSchedulesForCell =
    (practitionerId: number, date: string) =>
    (state: StoreState): MinimalLocationSchedule[] =>
        Object.values(state.surgeonScheduler.pendingLocationSchedules).filter(
            location => location.date === getOnlyDate(date) && location.practitionerId === practitionerId
        )

export const selectLocationsDisplayMode = ({ surgeonScheduler }: StoreState): ShowLocationsDisplayMode | false => {
    const { showLocations, showLocationsMode } = surgeonScheduler
    if (!showLocations) return false
    return showLocationsMode
}
